import { create } from 'zustand';

import { analytics } from '@/shared/lib/analytics';
import { sentryHelpers } from '@/shared/lib/sentry';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';
import type { User } from '@/shared/types/auth';

export type AuthState = {
  isAuthenticated: boolean;
  isInitializing: boolean;
  user: User | null;
  isCreditsLoading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  resetAuthState: () => void;
  setInitializing: (value: boolean) => void;
  updateUser: (userData: Partial<User>) => void;
  updateCredits: (newCredits: number) => void;
  deductCredits: (amount: number) => void;
  addCredits: (amount: number) => void;
  setCredits: (credits: number) => void;
  setCreditsLoading: (loading: boolean) => void;
  triggerCreditsLoading: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isInitializing: true,
  user: null,
  isCreditsLoading: false,

  login: (user) => {
    // Supabase handles token storage automatically
    set({ isAuthenticated: true, user, isCreditsLoading: false });

    // Set user context in Sentry for error tracking
    sentryHelpers.setUser({
      id: user.id,
      email: user.email,
    });
  },

  logout: async () => {
    // Clear local state first
    set({ isAuthenticated: false, user: null, isCreditsLoading: false });

    // Clear dev session if exists
    if (typeof window !== 'undefined') {
      localStorage.removeItem('famecreator-dev-user');
    }

    // Clear React Query cache manually
    if (typeof window !== 'undefined') {
      // Get query client from global if available
      const queryClient = (
        window as unknown as {
          __REACT_QUERY_CLIENT__?: {
            removeQueries: (options: { queryKey: string[] }) => void;
            clear: () => void;
          };
        }
      ).__REACT_QUERY_CLIENT__;
      if (queryClient) {
        queryClient.removeQueries({ queryKey: ['user', 'me'] });
        queryClient.clear(); // Clear all queries
      }
    }

    // Sign out from Supabase
    await supabaseAuthService.signOut();

    // Clear user context in Sentry
    sentryHelpers.setUser(null);

    // Reset RudderStack user identity
    analytics.reset();
  },

  resetAuthState: () => {
    // Reset auth state without calling Supabase signOut (to avoid triggering events)
    set({ isAuthenticated: false, user: null, isCreditsLoading: false });

    // Clear user context in Sentry
    sentryHelpers.setUser(null);
  },

  setInitializing: (value) => {
    set({ isInitializing: value });
  },

  updateUser: (userData) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...userData } : null,
    }));
  },

  updateCredits: (newCredits) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits: newCredits } : null,
    }));
  },

  deductCredits: (amount) => {
    set((state) => ({
      user: state.user
        ? { ...state.user, credits: Math.max(0, state.user.credits - amount) }
        : null,
    }));
  },

  addCredits: (amount) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits: state.user.credits + amount } : null,
    }));
  },

  setCredits: (credits) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits } : null,
    }));
  },

  setCreditsLoading: (loading) => {
    set({ isCreditsLoading: loading });
  },

  triggerCreditsLoading: () => {
    set({ isCreditsLoading: true });
  },
}));
